import { useCallback, useContext, useMemo } from "react";
import {
    DOWNTIMEMETHODCAPTURE,
    FAILUREDETAILSSTEPS,
    HMI_PLC_STATES,
    MEASURE_METRICS,
    FAILURE_TYPES_CODE_EXCEPTIONS,
    HMI_PATHS,
    IconsTrafficLightStatusCodes,
} from "constants/constants";
import { HMICachedDataContext } from "contexts/HMICachedDataContext/HMICachedDataContext";
import { HMIContext } from "contexts/HMIContext/HMIContext";
import { MQTTBrokerContext } from "contexts/MQTTBrokerContext/MQTTBrokerContext";
import { MachineCenterContext } from "contexts/MachineCenterContext/MachineCenterContext";
import { IEventReportValues, IPLCEvent } from "mqtt/entities/EventReport/EventReportInterfaces";
import {
    getCurrentStartDowntime,
    isPlcDisconnected,
    isUptimeActive,
    removeCurrentStartDowntime,
    removePlcDisconnected,
    removeEventReportEditData,
    removeEventReportEditing,
    setCurrentStartDowntime,
    setPlcDisconnected,
    setDowntime,
    setUptime,
    getCurrentFailureDetailsStep,
    getCurrentFailureType,
    getCurrentFailureCode,
    getCurrentArea,
    getCurrentFunctionalLocation,
    getCurrentNotes,
    getCurrentOperator,
    setCurrentStartBelowTriggerDowntimeCount,
    removeCurrentStartBelowTriggerDowntimeCount,
    getCurrentStartBelowTriggerDowntimeCount,
    setUuidLocalserverDowntimeCode,
    removeUuidLocalserverDowntimeCode,
    removeNotSelectedOrBelowTriggerEventReportId,
    getUuidLocalserverDowntimeCode,
    setLastInternetConnectionRecoveryDate,
    setCurrentFailureDetailsStep,
    setNotSelectedOrBelowTriggerEventReportId,
    setCurrentFunctionalLocation,
    setCurrentArea,
    setCurrentFailureCode,
    setCurrentFailureType,
    getMachineCentersClusterStatusLocalStorage,
    getNotSelectedOrBelowTriggerEventReportId,
    getMachineCenterHMIStore,
    getMachineCentersClusterStore,
    getClusterIdHMI,
    getMachineCenterIdHMI,
    getEventReportEditing,
    removeUuidLocalserverDowntimeCodeDuringInput,
    setUuidLocalserverDowntimeCodeDuringInput,
    getDefaultUptimeValues,
    getSplitInProgress,
    setSplitStartDowntime,
    getSplitOriginalStartDowntime,
    setSplitOriginalStartDowntime,
    getSplitOriginalSelections,
    setSplitOriginalSelections,
    clearSplitState,
} from "utilities/storageUtilities";
import useMachineCenterLogs from "./useMachineCenterLogs";
import { MachineCenterLogTriggersEnum } from "constants/constants";
import { MachineCentersEntity } from "entities/MachineCenters/MachineCentersEntity";
import moment from "moment";
import { useLocation, useNavigate } from "react-router-dom";
import { ViewOptionsEnum } from "components/ViewDropdown/ViewDropdown.view";
import { ClusterContext } from "contexts/ClusterContext/ClusterContext";
import { IMachineCentersClusterStatus } from "contexts/ClusterContext/ClusterContextInterfaces";
import { ClusterCachedDataContext } from "contexts/ClusterCachedDataContext/ClusterCachedDataContext";

const useUptimeDowntimeActionsHMI = () => {
    const {
        handleMachineStatus,
        handleDowntimeCounter,
        handleDowntimeCounterBelowTrigger,
        plc,
        handleContinueDowntimeDetailsInput,
        handlePlc,
        handleSplitInProgress,
    } = useContext(MachineCenterContext);
    const {
        handleSetFailureDetailStep,
        handleView,
        handleNotes,
        resetEditingSelections,
        handleFailureTypeChange,
        handleFailureCode,
        handleArea,
        handleFunctionalLocation,
        failureType,
        failureCode,
        functionalLocation,
        area,
        notes,
    } = useContext(HMIContext);
    const { handleSetMachineCentersClusterStatus } = useContext(ClusterContext);
    const { store, dispatch } = useContext(HMICachedDataContext);
    const { publishEventReport } = useContext(MQTTBrokerContext);
    const { triggerMachineCenterLogs } = useMachineCenterLogs();
    const navigate = useNavigate();
    const location = useLocation();
    const {
        store: { machineCentersCluster },
    } = useContext(ClusterCachedDataContext);

    const isClusterAllowed = useMemo(
        () =>
            store.machineCenter?.production_line?.mill?.cluster_enabled ||
            machineCentersCluster[0]?.production_line?.mill?.cluster_enabled,
        [machineCentersCluster, store],
    );

    const handleMachineInactiveDowntime = useCallback(
        (downtimeStartTimestamp?: number) => {
            removeUuidLocalserverDowntimeCodeDuringInput();
            handleMachineStatus(false);
            setDowntime();
            handleDowntimeCounter(true);
            handleDowntimeCounterBelowTrigger(false);
            removeCurrentStartBelowTriggerDowntimeCount();
            downtimeStartTimestamp && setCurrentStartDowntime(downtimeStartTimestamp);
            dispatch.isEventReportEditing(false);
            removeEventReportEditing();
            removeEventReportEditData();
            handleContinueDowntimeDetailsInput(false);
            resetEditingSelections();
            handleSetFailureDetailStep(FAILUREDETAILSSTEPS.FAILURETYPES);
        },
        [
            handleMachineStatus,
            handleDowntimeCounter,
            handleDowntimeCounterBelowTrigger,
            handleSetFailureDetailStep,
            dispatch,
            resetEditingSelections,
            handleContinueDowntimeDetailsInput,
        ],
    );

    const handleSplitDowntime = useCallback(() => {
        const originalStart = getCurrentStartDowntime();
        setSplitOriginalSelections({ failureType, area, functionalLocation, failureCode, notes });
        if (originalStart) setSplitOriginalStartDowntime(originalStart);
        setSplitStartDowntime(new Date().getTime());
        handleSplitInProgress(true);
        resetEditingSelections();
        handleSetFailureDetailStep(FAILUREDETAILSSTEPS.FAILURETYPES);
    }, [
        failureType,
        area,
        functionalLocation,
        failureCode,
        notes,
        handleSplitInProgress,
        resetEditingSelections,
        handleSetFailureDetailStep,
    ]);

    const handleCancelSplit = useCallback(() => {
        const original = getSplitOriginalSelections();
        const originalStart = getSplitOriginalStartDowntime();
        if (originalStart) setCurrentStartDowntime(originalStart);
        if (original?.failureType) handleFailureTypeChange(original.failureType);
        handleArea(original?.area);
        handleFunctionalLocation(original?.functionalLocation);
        if (original?.failureCode) handleFailureCode(original.failureCode);
        handleNotes(original?.notes || "");
        handleSplitInProgress(false);
        clearSplitState();
        handleSetFailureDetailStep(FAILUREDETAILSSTEPS.DOWNTIMESAVED);
    }, [
        handleFailureTypeChange,
        handleArea,
        handleFunctionalLocation,
        handleFailureCode,
        handleNotes,
        handleSplitInProgress,
        handleSetFailureDetailStep,
    ]);

    const handleMachineActiveUptime = useCallback(
        async (localseverUuidUpdate?: boolean) => {
            try {
                const currentStartDowntime = getCurrentStartDowntime();
                const failureType = getCurrentFailureType();
                const failureCode = getCurrentFailureCode();
                const area = getCurrentArea();
                const functionalLocation = getCurrentFunctionalLocation();
                const notes = getCurrentNotes();
                const operator = getCurrentOperator();
                const clusterId = getClusterIdHMI();
                const defaultUptimeValues = getDefaultUptimeValues();
                const operatorId = operator ? Number(operator) : defaultUptimeValues?.no_operator_id;
                const failureTypeId = failureType?.failure_type_id || defaultUptimeValues?.not_selected_type_id;

                if (
                    store.machineCenter &&
                    operatorId &&
                    failureTypeId &&
                    currentStartDowntime &&
                    (localseverUuidUpdate ? !plc : true)
                )
                    await publishEventReport({
                        machineCenter: store.machineCenter.machine_center_id,
                        downtimeMethodCapture: plc ? DOWNTIMEMETHODCAPTURE.PLC : DOWNTIMEMETHODCAPTURE.MANUAL,
                        user: operatorId,
                        ...(area && { area: area.area_id }),
                        ...(functionalLocation && { functionalLocation: functionalLocation.functional_location_id }),
                        failureType: failureTypeId,
                        ...(failureCode && { failureCode: failureCode.failure_code_id }),
                        startDate: new Date().toISOString(),
                        measureMetric: MEASURE_METRICS.UPTIME,
                        ...(notes && { notes: notes }),
                        ...(isClusterAllowed &&
                            clusterId && {
                                clusterId: clusterId,
                            }),
                    });
                handleMachineStatus(true);
                setUptime();
                removeCurrentStartBelowTriggerDowntimeCount();
                removeCurrentStartDowntime();
                handleDowntimeCounterBelowTrigger(false);
                handleDowntimeCounter(false);
                handleView(ViewOptionsEnum.summary);
                handleSetFailureDetailStep(FAILUREDETAILSSTEPS.FAILURETYPES);
                handleNotes("");
            } catch (error) {
                throw error;
            }
        },
        [
            handleMachineStatus,
            handleSetFailureDetailStep,
            handleDowntimeCounter,
            handleDowntimeCounterBelowTrigger,
            handleView,
            store.machineCenter,
            plc,
            publishEventReport,
            handleNotes,
            isClusterAllowed,
        ],
    );

    const handleNotSelectedFailureTypesReports = useCallback(() => {
        const resetDetails = false;
        const uptimeStatus = true;
        const uuidDowntimeEvent = getUuidLocalserverDowntimeCode();

        handleMachineStatus(uptimeStatus, resetDetails);
        handleContinueDowntimeDetailsInput(true);
        if (uuidDowntimeEvent) setUuidLocalserverDowntimeCodeDuringInput(uuidDowntimeEvent);
        setUptime();
        removeCurrentStartDowntime();
        removeCurrentStartBelowTriggerDowntimeCount();
        handleDowntimeCounter(false);
        handleDowntimeCounterBelowTrigger(false);
    }, [
        handleMachineStatus,
        handleDowntimeCounter,
        handleDowntimeCounterBelowTrigger,
        handleContinueDowntimeDetailsInput,
    ]);

    const handleBelowTriggerFailureTypesReports = useCallback(() => {
        const isEventReportEditing = getEventReportEditing();
        const uptimeStatus = true;
        handleMachineStatus(uptimeStatus, !isEventReportEditing);
        setUptime();
        removeCurrentStartBelowTriggerDowntimeCount();
        removeCurrentStartDowntime();
        handleDowntimeCounter(false);
        handleDowntimeCounterBelowTrigger(false);
    }, [handleMachineStatus, handleDowntimeCounter, handleDowntimeCounterBelowTrigger]);

    const handleUpdateOperatorInEvent = useCallback(
        (downtimeUuid: string) => {
            const operator = getCurrentOperator();
            if (Boolean(operator)) {
                publishEventReport({
                    uuid: downtimeUuid,
                    user: Number(operator),
                } as IEventReportValues);
            }
        },
        [publishEventReport],
    );

    const handlePLCMQTTMessagesCluster = useCallback(
        (event: IPLCEvent) => {
            const machineCenterClusterStore = getMachineCentersClusterStore();
            if (machineCenterClusterStore) {
                const machineCenterMatch = machineCenterClusterStore.find(
                    (machineCenterCluster) =>
                        event.plcAddress === machineCenterCluster.plc_address &&
                        machineCenterCluster.downtime_method_capture_id === DOWNTIMEMETHODCAPTURE.PLC,
                );
                const currentMachineCenterClusterStatusMatch = getMachineCentersClusterStatusLocalStorage()?.find(
                    (machineCenterClusterStatus) =>
                        machineCenterClusterStatus.machineCenterId === machineCenterMatch?.machine_center_id,
                );
                const currentMachineCenterClusterStatusLocalStorage = getMachineCentersClusterStatusLocalStorage();
                const isDebuggingMessagesActive =
                    process.env.REACT_APP_SHOW_HMI_PLC_DEBUGGING_MESSAGES?.toLocaleLowerCase() === "true";
                if (machineCenterMatch && currentMachineCenterClusterStatusMatch && isDebuggingMessagesActive)
                    console.log("Cluster event message received from localserver", event);

                if (
                    Boolean(machineCenterMatch) &&
                    Boolean(currentMachineCenterClusterStatusMatch) &&
                    currentMachineCenterClusterStatusLocalStorage
                ) {
                    if (
                        HMI_PLC_STATES.NOT_RUNNING === event.state &&
                        (HMI_PLC_STATES.RUNNING === event.oldState ||
                            HMI_PLC_STATES.NOT_CONNECTED === event.oldState) &&
                        currentMachineCenterClusterStatusMatch?.status === IconsTrafficLightStatusCodes.UP
                    ) {
                        const currentClusterMachinesStatusLocalstorage =
                            currentMachineCenterClusterStatusLocalStorage.map((currentMachineCenterClusterStatus) => {
                                if (
                                    currentMachineCenterClusterStatus.machineCenterId ===
                                    machineCenterMatch?.machine_center_id
                                ) {
                                    const currentStartDowntime =
                                        currentMachineCenterClusterStatus?.counter ||
                                        (event?.serverTimestamp
                                            ? new Date(
                                                  moment(
                                                      (event.serverTimestamp.toString() as string) + "-00:00",
                                                      "YYYY/MM/DD HH:mm:ss ZZ",
                                                  )
                                                      .local()
                                                      .toString(),
                                              ).getTime()
                                            : +new Date());
                                    const currentTime = new Date();
                                    const timer = +currentTime - (currentStartDowntime || 0);
                                    const downtimeTrigger =
                                        (machineCenterMatch?.downtime_trigger_threshold || 0) * 1000;
                                    const belowTrigger = downtimeTrigger > timer;
                                    return {
                                        ...currentMachineCenterClusterStatus,
                                        machinePlcDisconnected: false,
                                        eventLocalServerUuid: event?.uuid ? event?.uuid : undefined,
                                        notSelectedOrBelowTriggerEventReportId: event?.uuid
                                            ? undefined
                                            : currentMachineCenterClusterStatus.notSelectedOrBelowTriggerEventReportId,
                                        status: belowTrigger
                                            ? IconsTrafficLightStatusCodes.BELOW_TRIGGER
                                            : IconsTrafficLightStatusCodes.DOWN,
                                        downtimePending: Boolean(
                                            !belowTrigger && !currentMachineCenterClusterStatus?.failureDetails,
                                        ),
                                        counter: currentMachineCenterClusterStatus.counter
                                            ? currentMachineCenterClusterStatus.counter
                                            : currentStartDowntime,
                                        failureDetails: undefined,
                                    };
                                } else {
                                    return { ...currentMachineCenterClusterStatus };
                                }
                            });
                        handleSetMachineCentersClusterStatus(currentClusterMachinesStatusLocalstorage);

                        isDebuggingMessagesActive && console.log(`${machineCenterMatch?.name}: Downtime initiated`);
                    } else if (
                        HMI_PLC_STATES.RUNNING === event.state &&
                        (HMI_PLC_STATES.NOT_RUNNING === event.oldState ||
                            HMI_PLC_STATES.NOT_CONNECTED === event.oldState) &&
                        currentMachineCenterClusterStatusMatch?.status !== IconsTrafficLightStatusCodes.UP
                    ) {
                        const currentClusterMachinesStatusLocalstorage =
                            currentMachineCenterClusterStatusLocalStorage.map((currentMachineCenterClusterStatus) => {
                                if (
                                    currentMachineCenterClusterStatus.machineCenterId ===
                                    machineCenterMatch?.machine_center_id
                                ) {
                                    const uuidDowntimeEvent = currentMachineCenterClusterStatus?.eventLocalServerUuid;
                                    uuidDowntimeEvent && handleUpdateOperatorInEvent(uuidDowntimeEvent);
                                    return {
                                        ...currentMachineCenterClusterStatus,
                                        machinePlcDisconnected: false,
                                        eventLocalServerUuid: undefined,
                                        notSelectedOrBelowTriggerEventReportId: undefined,
                                        status: IconsTrafficLightStatusCodes.UP,
                                        downtimePending: false,
                                        counter: undefined,
                                        failureDetails: undefined,
                                    };
                                } else {
                                    return { ...currentMachineCenterClusterStatus };
                                }
                            });
                        handleSetMachineCentersClusterStatus(currentClusterMachinesStatusLocalstorage);
                        isDebuggingMessagesActive && console.log(`${machineCenterMatch?.name}: Uptime initiated`);
                    } else if (
                        HMI_PLC_STATES.NOT_CONNECTED === event.state &&
                        !currentMachineCenterClusterStatusMatch?.machinePlcDisconnected
                    ) {
                        const currentClusterMachinesStatusLocalstorage =
                            currentMachineCenterClusterStatusLocalStorage.map((currentMachineCenterClusterStatus) => {
                                if (
                                    currentMachineCenterClusterStatus.machineCenterId ===
                                    machineCenterMatch?.machine_center_id
                                ) {
                                    return {
                                        ...currentMachineCenterClusterStatus,
                                        machinePlcDisconnected: true,
                                        ...(currentMachineCenterClusterStatusMatch?.status !==
                                            IconsTrafficLightStatusCodes.UP && {
                                            status: IconsTrafficLightStatusCodes.DOWN,
                                            downtimePending: Boolean(
                                                !currentMachineCenterClusterStatus?.failureDetails,
                                            ),
                                        }),
                                    };
                                } else {
                                    return { ...currentMachineCenterClusterStatus };
                                }
                            });
                        handleSetMachineCentersClusterStatus(currentClusterMachinesStatusLocalstorage);
                    } else if (
                        currentMachineCenterClusterStatusMatch?.machinePlcDisconnected &&
                        HMI_PLC_STATES.NOT_CONNECTED !== event.state
                    ) {
                        const currentClusterMachinesStatusLocalstorage =
                            currentMachineCenterClusterStatusLocalStorage.map((currentMachineCenterClusterStatus) => {
                                if (
                                    currentMachineCenterClusterStatus.machineCenterId ===
                                    machineCenterMatch?.machine_center_id
                                ) {
                                    return {
                                        ...currentMachineCenterClusterStatus,
                                        machinePlcDisconnected: false,
                                    };
                                } else {
                                    return { ...currentMachineCenterClusterStatus };
                                }
                            });
                        handleSetMachineCentersClusterStatus(currentClusterMachinesStatusLocalstorage);
                    }
                }
            }
        },
        [handleSetMachineCentersClusterStatus, handleUpdateOperatorInEvent],
    );

    const handlePLCMQTTMessagesHMI = useCallback(
        (event: IPLCEvent) => {
            const machineCenterHMIStore = getMachineCenterHMIStore();
            const machineCenterClusterStore = getMachineCentersClusterStore();
            const cluster = getClusterIdHMI();
            const isClusterAllowed =
                machineCenterClusterStore?.[0]?.production_line?.mill?.cluster_enabled ||
                machineCenterHMIStore?.production_line?.mill?.cluster_enabled;
            const isCluster = Boolean(cluster) && isClusterAllowed;
            if (
                location.pathname
                    .toLowerCase()
                    .includes(`/${HMI_PATHS.ROOT_PATH.toLowerCase()}/${HMI_PATHS.DASHBOARD}`) &&
                machineCenterHMIStore?.plc_address &&
                machineCenterHMIStore?.downtime_method_capture_id === DOWNTIMEMETHODCAPTURE.PLC
            ) {
                const machineCenterMatch = event.plcAddress === machineCenterHMIStore.plc_address;
                const isDebuggingMessagesActive =
                    process.env.REACT_APP_SHOW_HMI_PLC_DEBUGGING_MESSAGES?.toLocaleLowerCase() === "true";
                if (machineCenterMatch && isDebuggingMessagesActive)
                    console.log("HMI event message received from localserver", event);
                if (machineCenterMatch && HMI_PLC_STATES.RUNNING === event.state && getSplitInProgress()) {
                    handleCancelSplit();
                }
                const uptime = isUptimeActive();
                const currentFailureDetailsStepFromLocalStorage = getCurrentFailureDetailsStep();
                const plcDisconnected = isPlcDisconnected();
                const eventTimestamp = event?.serverTimestamp
                    ? new Date(
                          moment((event.serverTimestamp.toString() as string) + "-00:00", "YYYY/MM/DD HH:mm:ss ZZ")
                              .local()
                              .toString(),
                      ).getTime()
                    : new Date().getTime();

                const currentStartDowntimeBelowTrigger = getCurrentStartBelowTriggerDowntimeCount();
                const currentStartDowntime = currentStartDowntimeBelowTrigger
                    ? currentStartDowntimeBelowTrigger
                    : getCurrentStartDowntime();
                const currentTime = new Date();
                const timer = +currentTime - (currentStartDowntime || 0);
                const minimumDowntimeDefinition = (machineCenterHMIStore?.minimum_downtime || 0) * 1000;
                const downtimeTrigger = (machineCenterHMIStore?.downtime_trigger_threshold || 0) * 1000;
                const isDowntime = minimumDowntimeDefinition < timer;
                const belowTrigger = downtimeTrigger > timer;
                const uuidDowntimeEvent = getUuidLocalserverDowntimeCode();
                if (machineCenterMatch) {
                    if (
                        HMI_PLC_STATES.NOT_RUNNING === event.state &&
                        (HMI_PLC_STATES.RUNNING === event.oldState ||
                            HMI_PLC_STATES.NOT_CONNECTED === event.oldState) &&
                        uptime
                    ) {
                        if (event?.uuid) {
                            setUuidLocalserverDowntimeCode(event.uuid);
                            removeNotSelectedOrBelowTriggerEventReportId();
                        }
                        plcDisconnected && removePlcDisconnected();
                        handlePlc(true);
                        if (currentStartDowntime) {
                            if (!belowTrigger) {
                                isDebuggingMessagesActive && console.log("Downtime continued over trigger");
                                handleMachineInactiveDowntime(currentStartDowntime);
                            } else {
                                isDebuggingMessagesActive && console.log("Downtime continued below trigger");
                            }
                        } else {
                            setCurrentStartBelowTriggerDowntimeCount(eventTimestamp);
                            handleDowntimeCounterBelowTrigger(true);
                            isDebuggingMessagesActive && console.log("Downtime initiated");
                        }
                    } else if (
                        HMI_PLC_STATES.RUNNING === event.state &&
                        (HMI_PLC_STATES.NOT_RUNNING === event.oldState ||
                            HMI_PLC_STATES.NOT_CONNECTED === event.oldState) &&
                        (!uptime || currentStartDowntimeBelowTrigger)
                    ) {
                        plcDisconnected && removePlcDisconnected();
                        handlePlc(true);
                        uuidDowntimeEvent && handleUpdateOperatorInEvent(uuidDowntimeEvent);
                        if (
                            currentFailureDetailsStepFromLocalStorage === FAILUREDETAILSSTEPS.DOWNTIMESAVED ||
                            currentFailureDetailsStepFromLocalStorage === FAILUREDETAILSSTEPS.PLCSHOWHOMEDURINGDOWNTIME
                        ) {
                            isDebuggingMessagesActive && console.log("Uptime initiated");
                            handleMachineActiveUptime(Boolean(event?.uuid));
                        } else {
                            if (isDowntime) {
                                if (belowTrigger) {
                                    isDebuggingMessagesActive &&
                                        console.log(
                                            "Downtime under the downtime trigger threshold, registered as Below Trigger.",
                                            timer,
                                        );

                                    handleBelowTriggerFailureTypesReports();
                                } else {
                                    isDebuggingMessagesActive &&
                                        console.log("Downtime with no selection registered.", timer);
                                    handleNotSelectedFailureTypesReports();
                                }
                            } else {
                                const isEventReportEditing = getEventReportEditing();
                                const uptimeStatus = true;
                                isDebuggingMessagesActive &&
                                    console.log(
                                        "Downtime under the downtime minimum definition, not registered.",
                                        timer,
                                    );
                                setUptime();
                                handleMachineStatus(uptimeStatus, !isEventReportEditing);
                                removeCurrentStartDowntime();
                                removeCurrentStartBelowTriggerDowntimeCount();
                                handleDowntimeCounterBelowTrigger(false);
                                handleDowntimeCounter(false);
                            }
                        }
                        removeUuidLocalserverDowntimeCode();
                        removeNotSelectedOrBelowTriggerEventReportId();
                    } else if (HMI_PLC_STATES.NOT_CONNECTED === event.state && !plcDisconnected) {
                        currentFailureDetailsStepFromLocalStorage === FAILUREDETAILSSTEPS.PLCSHOWHOMEDURINGDOWNTIME &&
                            handleSetFailureDetailStep(FAILUREDETAILSSTEPS.DOWNTIMESAVED);
                        setPlcDisconnected();
                        handlePlc(false);
                        if ((!uptime || currentStartDowntime) && !getSplitInProgress()) {
                            handleMachineInactiveDowntime(currentStartDowntime);
                        }
                    } else if (HMI_PLC_STATES.NOT_CONNECTED !== event.state && plcDisconnected) {
                        removePlcDisconnected();
                        handlePlc(true);
                    }
                }

                isCluster && handlePLCMQTTMessagesCluster(event);
            } else if (isCluster) {
                handlePLCMQTTMessagesCluster(event);
            }
        },
        [
            handleMachineActiveUptime,
            handleNotSelectedFailureTypesReports,
            handleMachineInactiveDowntime,
            handleCancelSplit,
            handleSetFailureDetailStep,
            location,
            handleBelowTriggerFailureTypesReports,
            handleDowntimeCounter,
            handleDowntimeCounterBelowTrigger,
            handleMachineStatus,
            handleUpdateOperatorInEvent,
            handlePLCMQTTMessagesCluster,
            handlePlc,
        ],
    );

    const handleHMILogin = useCallback(
        async (
            machineCenter: {
                machine_center_id: number;
                downtime_method_capture_id: number;
            },
            isOnline: boolean,
            currentMachineCenterClusterStatus?: IMachineCentersClusterStatus,
            machinePlcIsDisconnected?: boolean,
        ) => {
            const plcDisconnected = isPlcDisconnected();
            const currentStartDowntime = getCurrentStartDowntime();
            const currentStartBelowTriggerDowntimeCount = getCurrentStartBelowTriggerDowntimeCount();
            const uptimeFromLocalStorage = isUptimeActive();
            const currentFailureDetailsStepFromLocalStorage = getCurrentFailureDetailsStep();
            removeUuidLocalserverDowntimeCodeDuringInput();

            if (isOnline && !currentMachineCenterClusterStatus) {
                const currentMachineStatus = await MachineCentersEntity.getMachineCenterCurrentStatus([
                    machineCenter.machine_center_id,
                ]);

                if (currentMachineStatus.length < 1) {
                    handleMachineStatus(true);
                    setUptime();
                    handleDowntimeCounterBelowTrigger(false);
                    handleDowntimeCounter(false);
                    setCurrentFailureDetailsStep(FAILUREDETAILSSTEPS.FAILURETYPES);
                    handleSetFailureDetailStep(FAILUREDETAILSSTEPS.FAILURETYPES);
                    removeCurrentStartDowntime();
                } else {
                    if (
                        currentMachineStatus.length > 0 &&
                        currentMachineStatus[0].measure_metric_id === MEASURE_METRICS.UPTIME
                    ) {
                        handleMachineStatus(true);
                        setUptime();
                        handleDowntimeCounterBelowTrigger(false);
                        handleDowntimeCounter(false);
                        removeCurrentStartDowntime();
                        setCurrentFailureDetailsStep(FAILUREDETAILSSTEPS.FAILURETYPES);
                        handleSetFailureDetailStep(FAILUREDETAILSSTEPS.FAILURETYPES);
                    } else {
                        if (
                            currentMachineStatus[0]?.failure_type?.code === FAILURE_TYPES_CODE_EXCEPTIONS.BELOW_TRIGGER
                        ) {
                            handleMachineStatus(true);
                            setUptime();
                            setCurrentStartBelowTriggerDowntimeCount(
                                new Date(
                                    moment(
                                        (currentMachineStatus[0].start_date.toString() as string) + "-00:00",
                                        "YYYY/MM/DD HH:mm:ss ZZ",
                                    )
                                        .local()
                                        .toString(),
                                ).getTime(),
                            );
                            handleDowntimeCounterBelowTrigger(true);
                            handleDowntimeCounter(true);
                            setNotSelectedOrBelowTriggerEventReportId(currentMachineStatus[0].event_report_id);
                            setCurrentFailureDetailsStep(FAILUREDETAILSSTEPS.FAILURETYPES);
                            handleSetFailureDetailStep(FAILUREDETAILSSTEPS.FAILURETYPES);
                        } else {
                            removeCurrentStartDowntime();
                            handleMachineStatus(false);
                            setDowntime();
                            handleDowntimeCounter(true);
                            setCurrentStartDowntime(
                                new Date(
                                    moment(
                                        (currentMachineStatus[0].start_date.toString() as string) + "-00:00",
                                        "YYYY/MM/DD HH:mm:ss ZZ",
                                    )
                                        .local()
                                        .toString(),
                                ).getTime(),
                            );
                            const failureTypeWithFailureCodes = {
                                ...currentMachineStatus[0].failure_type,
                                failure_codes: [],
                            };
                            handleFailureTypeChange(failureTypeWithFailureCodes);
                            setCurrentFailureType(failureTypeWithFailureCodes);
                            if (currentMachineStatus[0].failure_code) {
                                handleFailureCode(currentMachineStatus[0].failure_code);
                                setCurrentFailureCode(currentMachineStatus[0].failure_code);
                            }
                            if (currentMachineStatus[0].area) {
                                handleArea(currentMachineStatus[0].area);
                                setCurrentArea(currentMachineStatus[0].area);
                            }
                            if (currentMachineStatus[0].functional_location) {
                                handleFunctionalLocation(currentMachineStatus[0].functional_location);
                                setCurrentFunctionalLocation(currentMachineStatus[0].functional_location);
                            }
                            if (
                                currentMachineStatus[0]?.failure_type?.code ===
                                FAILURE_TYPES_CODE_EXCEPTIONS.NOT_SELECTED
                            ) {
                                setNotSelectedOrBelowTriggerEventReportId(currentMachineStatus[0].event_report_id);
                                setCurrentFailureDetailsStep(FAILUREDETAILSSTEPS.FAILURETYPES);
                                handleSetFailureDetailStep(FAILUREDETAILSSTEPS.FAILURETYPES);
                            } else {
                                setCurrentFailureDetailsStep(FAILUREDETAILSSTEPS.DOWNTIMESAVED);
                                handleSetFailureDetailStep(FAILUREDETAILSSTEPS.DOWNTIMESAVED);
                            }
                        }
                    }
                }
                const currentDate = new Date().toISOString();
                setLastInternetConnectionRecoveryDate(currentDate);
                const machineCenterId = getMachineCenterIdHMI();

                if (process.env.REACT_APP_LOGS_ENABLED) {
                    const message = {
                        currentFailureDetailsStepFromLocalStorage: currentFailureDetailsStepFromLocalStorage ?? null,
                        currentFailureTypeFromLocalStorage: failureType ?? null,
                        currentFailureCodeFromLocalStorage: failureCode ?? null,
                        currentFunctionalLocationFromLocalStorage: functionalLocation ?? null,
                        currentAreaFromLocalStorage: area ?? null,
                        currentNotesFromLocalStorage: notes ?? null,
                        plcDisconnected: plcDisconnected,
                        machineCenterIdHMI: machineCenterId?.machine_center_id ?? null,
                        downtimeCounter: currentStartDowntime ?? null,
                        downtimeCounterBelowTrigger: currentStartBelowTriggerDowntimeCount ?? null,
                    };

                    triggerMachineCenterLogs(
                        MachineCenterLogTriggersEnum.AT_HMI_LOGIN,
                        JSON.stringify(message),
                        new Date().toISOString(),
                    );
                }
            } else if (currentMachineCenterClusterStatus) {
                if (currentMachineCenterClusterStatus?.status === IconsTrafficLightStatusCodes.UP) {
                    handleMachineStatus(true);
                    setUptime();
                    setCurrentFailureDetailsStep(FAILUREDETAILSSTEPS.FAILURETYPES);
                    handleSetFailureDetailStep(FAILUREDETAILSSTEPS.FAILURETYPES);
                    removeCurrentStartDowntime();
                    handleDowntimeCounterBelowTrigger(false);
                    handleDowntimeCounter(false);
                } else {
                    if (currentMachineCenterClusterStatus?.status === IconsTrafficLightStatusCodes.BELOW_TRIGGER) {
                        handleMachineStatus(true);
                        setUptime();
                        setCurrentStartBelowTriggerDowntimeCount(Number(currentMachineCenterClusterStatus?.counter));
                        handleDowntimeCounterBelowTrigger(true);
                        handleDowntimeCounter(true);
                        if (currentMachineCenterClusterStatus.notSelectedOrBelowTriggerEventReportId) {
                            setNotSelectedOrBelowTriggerEventReportId(
                                currentMachineCenterClusterStatus.notSelectedOrBelowTriggerEventReportId,
                            );
                        } else if (currentMachineCenterClusterStatus.eventLocalServerUuid) {
                            setUuidLocalserverDowntimeCode(currentMachineCenterClusterStatus.eventLocalServerUuid);
                        }
                        setCurrentFailureDetailsStep(FAILUREDETAILSSTEPS.FAILURETYPES);
                        handleSetFailureDetailStep(FAILUREDETAILSSTEPS.FAILURETYPES);
                    } else {
                        removeCurrentStartDowntime();
                        handleMachineStatus(false);
                        handleDowntimeCounter(true);
                        setDowntime();
                        setCurrentStartDowntime(Number(currentMachineCenterClusterStatus?.counter));
                        if (currentMachineCenterClusterStatus?.failureDetails) {
                            if (currentMachineCenterClusterStatus?.failureDetails?.failureType) {
                                handleFailureTypeChange(currentMachineCenterClusterStatus?.failureDetails?.failureType);
                                setCurrentFailureType(currentMachineCenterClusterStatus?.failureDetails?.failureType);
                            }
                            if (currentMachineCenterClusterStatus?.failureDetails?.failureCode) {
                                handleFailureCode(currentMachineCenterClusterStatus?.failureDetails?.failureCode);
                                setCurrentFailureCode(currentMachineCenterClusterStatus?.failureDetails?.failureCode);
                            }
                            if (currentMachineCenterClusterStatus?.failureDetails?.area) {
                                handleArea(currentMachineCenterClusterStatus?.failureDetails?.area);
                                setCurrentArea(currentMachineCenterClusterStatus?.failureDetails?.area);
                            }
                            if (currentMachineCenterClusterStatus?.failureDetails?.functionalLocation) {
                                handleFunctionalLocation(
                                    currentMachineCenterClusterStatus?.failureDetails?.functionalLocation,
                                );
                                setCurrentFunctionalLocation(
                                    currentMachineCenterClusterStatus?.failureDetails?.functionalLocation,
                                );
                            }
                        }

                        if (
                            currentMachineCenterClusterStatus?.status === IconsTrafficLightStatusCodes.DOWN &&
                            currentMachineCenterClusterStatus?.downtimePending
                        ) {
                            if (currentMachineCenterClusterStatus.notSelectedOrBelowTriggerEventReportId) {
                                setNotSelectedOrBelowTriggerEventReportId(
                                    currentMachineCenterClusterStatus.notSelectedOrBelowTriggerEventReportId,
                                );
                            } else if (currentMachineCenterClusterStatus.eventLocalServerUuid) {
                                setUuidLocalserverDowntimeCode(currentMachineCenterClusterStatus.eventLocalServerUuid);
                            }
                            setCurrentFailureDetailsStep(FAILUREDETAILSSTEPS.FAILURETYPES);
                            handleSetFailureDetailStep(FAILUREDETAILSSTEPS.FAILURETYPES);
                        } else {
                            setCurrentFailureDetailsStep(FAILUREDETAILSSTEPS.DOWNTIMESAVED);
                            handleSetFailureDetailStep(FAILUREDETAILSSTEPS.DOWNTIMESAVED);
                        }
                    }
                }
                const currentDate = new Date().toISOString();
                setLastInternetConnectionRecoveryDate(currentDate);
            } else {
                handleMachineStatus(uptimeFromLocalStorage);
            }
            if (machinePlcIsDisconnected) {
                currentFailureDetailsStepFromLocalStorage === FAILUREDETAILSSTEPS.PLCSHOWHOMEDURINGDOWNTIME &&
                    handleSetFailureDetailStep(FAILUREDETAILSSTEPS.DOWNTIMESAVED);
                setPlcDisconnected();
                handlePlc(false);
            }
            // OS-2467: resume an in-progress split after reload — split state persists in localStorage.
            // The DB-ongoing event is still the original; re-arm the split wizard at the first step.
            if (getSplitInProgress()) {
                handleSplitInProgress(true);
                resetEditingSelections();
                handleSetFailureDetailStep(FAILUREDETAILSSTEPS.FAILURETYPES);
            }
            navigate(`/${HMI_PATHS.ROOT_PATH}/${HMI_PATHS.DASHBOARD}`);
        },
        [
            area,
            failureCode,
            failureType,
            functionalLocation,
            handleArea,
            handleDowntimeCounterBelowTrigger,
            handleFailureCode,
            handleFailureTypeChange,
            handleFunctionalLocation,
            handleMachineStatus,
            handleSetFailureDetailStep,
            navigate,
            notes,
            triggerMachineCenterLogs,
            handleDowntimeCounter,
            handlePlc,
            handleSplitInProgress,
            resetEditingSelections,
        ],
    );

    const handleClusterLogin = useCallback(
        async (
            machineCenters: {
                machine_center_id: number;
            }[],
            isOnline: boolean,
            currentMachineCenterClusterStatus?: IMachineCentersClusterStatus[],
        ) => {
            if (isOnline && !currentMachineCenterClusterStatus) {
                const currentClusterMachinesStatus = await MachineCentersEntity.getMachineCenterCurrentStatus(
                    machineCenters.map((machinecenter) => machinecenter.machine_center_id),
                );

                if (machineCenters.length > 0) {
                    const currentClusterMachinesStatusLocalstorage: IMachineCentersClusterStatus[] = machineCenters.map(
                        (machineCenter) => {
                            const currentClusterMachinesStatusMatch = currentClusterMachinesStatus.find(
                                (currentMachineCenterStatus) =>
                                    machineCenter.machine_center_id === currentMachineCenterStatus.machine_center_id,
                            );
                            if (currentClusterMachinesStatusMatch) {
                                const failureTypeWithFailureCodes = {
                                    ...currentClusterMachinesStatusMatch?.failure_type,
                                    failure_codes: [],
                                };
                                return {
                                    machineCenterId: currentClusterMachinesStatusMatch.machine_center_id,
                                    machinePlcDisconnected: false,
                                    notSelectedOrBelowTriggerEventReportId:
                                        MEASURE_METRICS.DOWNTIME ===
                                            currentClusterMachinesStatusMatch.measure_metric_id &&
                                        (currentClusterMachinesStatusMatch?.failure_type?.code ===
                                            FAILURE_TYPES_CODE_EXCEPTIONS.BELOW_TRIGGER ||
                                            currentClusterMachinesStatusMatch?.failure_type?.code ===
                                                FAILURE_TYPES_CODE_EXCEPTIONS.NOT_SELECTED)
                                            ? currentClusterMachinesStatusMatch.event_report_id
                                            : undefined,
                                    status:
                                        MEASURE_METRICS.DOWNTIME ===
                                            currentClusterMachinesStatusMatch.measure_metric_id &&
                                        currentClusterMachinesStatusMatch?.failure_type?.code ===
                                            FAILURE_TYPES_CODE_EXCEPTIONS.BELOW_TRIGGER
                                            ? IconsTrafficLightStatusCodes.BELOW_TRIGGER
                                            : MEASURE_METRICS.DOWNTIME ===
                                                currentClusterMachinesStatusMatch.measure_metric_id
                                              ? IconsTrafficLightStatusCodes.DOWN
                                              : IconsTrafficLightStatusCodes.UP,
                                    downtimePending:
                                        MEASURE_METRICS.DOWNTIME ===
                                            currentClusterMachinesStatusMatch.measure_metric_id &&
                                        currentClusterMachinesStatusMatch?.failure_type?.code ===
                                            FAILURE_TYPES_CODE_EXCEPTIONS.NOT_SELECTED,
                                    counter:
                                        MEASURE_METRICS.DOWNTIME === currentClusterMachinesStatusMatch.measure_metric_id
                                            ? new Date(
                                                  moment(
                                                      (currentClusterMachinesStatusMatch.start_date.toString() as string) +
                                                          "-00:00",
                                                      "YYYY/MM/DD HH:mm:ss ZZ",
                                                  )
                                                      .local()
                                                      .toString(),
                                              ).getTime()
                                            : undefined,
                                    eventLocalServerUuid: undefined,
                                    failureDetails:
                                        MEASURE_METRICS.DOWNTIME ===
                                            currentClusterMachinesStatusMatch.measure_metric_id &&
                                        currentClusterMachinesStatusMatch?.failure_type?.code !==
                                            FAILURE_TYPES_CODE_EXCEPTIONS.BELOW_TRIGGER &&
                                        currentClusterMachinesStatusMatch?.failure_type?.code !==
                                            FAILURE_TYPES_CODE_EXCEPTIONS.NOT_SELECTED
                                            ? {
                                                  failureType: failureTypeWithFailureCodes,
                                                  failureCode: currentClusterMachinesStatusMatch?.failure_code
                                                      ? currentClusterMachinesStatusMatch?.failure_code
                                                      : undefined,
                                                  area: currentClusterMachinesStatusMatch?.area
                                                      ? currentClusterMachinesStatusMatch?.area
                                                      : undefined,
                                                  functionalLocation:
                                                      currentClusterMachinesStatusMatch?.functional_location
                                                          ? currentClusterMachinesStatusMatch?.functional_location
                                                          : undefined,
                                              }
                                            : undefined,
                                };
                            } else {
                                return {
                                    machineCenterId: machineCenter.machine_center_id,
                                    machinePlcDisconnected: false,
                                    notSelectedOrBelowTriggerEventReportId: undefined,
                                    eventLocalServerUuid: undefined,
                                    failureDetails: undefined,
                                    status: IconsTrafficLightStatusCodes.UP,
                                    downtimePending: false,
                                    counter: undefined,
                                };
                            }
                        },
                    );
                    handleSetMachineCentersClusterStatus(currentClusterMachinesStatusLocalstorage);
                }
            } else if (currentMachineCenterClusterStatus) {
                const uuidLocalserverDowntimeCode = getUuidLocalserverDowntimeCode();
                const notSelectedOrBelowTriggerEventReportId = getNotSelectedOrBelowTriggerEventReportId();
                const uptime = isUptimeActive();
                const currentFailureDetailsStepFromLocalStorage = getCurrentFailureDetailsStep();
                const currentStartDowntimeBelowTrigger = getCurrentStartBelowTriggerDowntimeCount();
                const currentStartDowntime = currentStartDowntimeBelowTrigger
                    ? currentStartDowntimeBelowTrigger
                    : getCurrentStartDowntime();

                const updatedMachineCentersClusterStatus = currentMachineCenterClusterStatus.map(
                    (machineCenterClusterStatus) => {
                        if (store.machineCenter?.machine_center_id === machineCenterClusterStatus.machineCenterId) {
                            return {
                                ...machineCenterClusterStatus,
                                notSelectedOrBelowTriggerEventReportId: notSelectedOrBelowTriggerEventReportId,
                                eventLocalServerUuid: notSelectedOrBelowTriggerEventReportId
                                    ? undefined
                                    : uuidLocalserverDowntimeCode
                                      ? uuidLocalserverDowntimeCode
                                      : undefined,
                                status:
                                    uptime && currentStartDowntime
                                        ? IconsTrafficLightStatusCodes.BELOW_TRIGGER
                                        : uptime
                                          ? IconsTrafficLightStatusCodes.UP
                                          : IconsTrafficLightStatusCodes.DOWN,
                                downtimePending:
                                    !uptime &&
                                    currentStartDowntime &&
                                    !(
                                        currentFailureDetailsStepFromLocalStorage ===
                                            FAILUREDETAILSSTEPS.DOWNTIMESAVED ||
                                        currentFailureDetailsStepFromLocalStorage ===
                                            FAILUREDETAILSSTEPS.PLCSHOWHOMEDURINGDOWNTIME ||
                                        store.isEventReportEditing
                                    )
                                        ? true
                                        : false,
                                counter: currentStartDowntime,
                            };
                        } else {
                            return {
                                ...machineCenterClusterStatus,
                            };
                        }
                    },
                );
                handleSetMachineCentersClusterStatus(updatedMachineCentersClusterStatus);
            }
            const currentDate = new Date().toISOString();
            setLastInternetConnectionRecoveryDate(currentDate);
            navigate(`/${HMI_PATHS.ROOT_PATH}/${HMI_PATHS.CLUSTER}`);
        },
        [navigate, handleSetMachineCentersClusterStatus, store],
    );

    return {
        handleMachineInactiveDowntime,
        handleMachineActiveUptime,
        handleSplitDowntime,
        handleCancelSplit,
        handlePLCMQTTMessagesHMI,
        handleClusterLogin,
        handleHMILogin,
        handlePLCMQTTMessagesCluster,
    };
};

export default useUptimeDowntimeActionsHMI;
